import { useT } from '@dia/i18n';
import { Todo } from './Prose';

/**
 * A full bleed photographic panel. With a `src` it shows the photograph. With
 * none it shows a flat placeholder drawn from the brand tokens, and says
 * plainly that the photograph is still to come, so nobody mistakes it for the
 * finished page.
 *
 * No gradients, on purpose (Section 9): the placeholder is flat shapes.
 */
export function PhotoPanel({
  src,
  alt,
  todo,
  className = '',
  captionCorner = 'bottom-right',
}: {
  src?: string;
  alt?: string;
  /** What the missing photograph should be, for the placeholder list. */
  todo: string;
  className?: string;
  /**
   * Where the "photo to supply" note sits. Callers that overlay their own
   * caption at the bottom of the panel (a cover title on a scrim, say) should
   * pass 'top-right' so the two notes never share the same corner.
   */
  captionCorner?: 'bottom-right' | 'top-right';
}) {
  const { t } = useT();

  if (src) {
    return <img src={src} alt={alt ?? ''} className={`h-full w-full object-cover ${className}`} />;
  }

  const captionPosition = captionCorner === 'top-right' ? 'top-3' : 'bottom-3';

  return (
    <div className={`relative h-full w-full overflow-hidden bg-surfaceDeep ${className}`}>
      <svg
        role="img"
        aria-label={t('guest.arrive.photo_alt')}
        viewBox="0 0 400 300"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 h-full w-full"
      >
        <rect width="400" height="300" className="fill-surfaceDeep" />
        {/* the sea */}
        <rect y="172" width="400" height="128" className="fill-surface" />
        {/* the sun, a small mark low on the horizon */}
        <circle cx="286" cy="156" r="9" className="fill-mark" />
        {/* the island, rising from the water */}
        <g className="dia-lift">
          <path
            d="M-10 176 C 40 168 70 150 104 152 C 122 130 138 122 156 138 C 176 118 196 116 214 134 C 232 128 252 140 266 152 C 296 150 340 164 410 176 Z"
            className="fill-ground"
          />
        </g>
        {/* a still line of light on the water */}
        <path d="M0 176 H400" strokeWidth="1" className="stroke-accent" opacity="0.5" />
        <path d="M232 196 H340 M254 208 H320 M268 220 H306" strokeWidth="1" className="stroke-accent" opacity="0.28" />
      </svg>
      <p className={`absolute right-3 ${captionPosition} max-w-[75%] text-right text-caption`}>
        <Todo>{`PHOTO TO SUPPLY: ${todo}`}</Todo>
      </p>
    </div>
  );
}
